import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const GRID = 'hsl(215 25% 18% / 0.5)';
const AXIS = 'hsl(215 16% 55%)';
const COLORS = ['hsl(var(--primary))', 'hsl(var(--warning))', 'hsl(var(--danger))', 'hsl(var(--success))', 'hsl(215 16% 55%)'];

export function AnalyticsPanel({ agentId }) {
  const [days, setDays] = React.useState('30');
  const { data, isLoading } = useQuery({
    queryKey: ['analytics', agentId, days],
    queryFn: () => api.get(`/agents/${agentId}/analytics?days=${days}`).then(r => r.data),
  });

  if (isLoading) return <div className="text-sm text-[hsl(var(--text-3))]">Loading analytics…</div>;
  if (!data) return <div className="text-sm text-[hsl(var(--text-3))]">No data</div>;

  const totals = data.totals || {};
  const intel = data.intelligent || {};
  const dailyShort = (data.daily || []).map(d => ({ ...d, d: d.day.slice(5) }));
  const risk = (data.risk_timeline || []).map((r, i) => ({ i, risk: r.risk }));
  const kinds = data.kind_distribution || [];
  const verdicts = data.verdict_distribution || [];

  return (
    <div className="space-y-4" data-testid="analytics-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs text-[hsl(var(--text-3))]">Aggregated over the last {data.window_days} days.</div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32" data-testid="analytics-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Total tasks" value={totals.tasks ?? 0} />
        <StatCard label="Completed" value={totals.completed ?? 0} hint={`${totals.failed ?? 0} failed`} />
        <StatCard label="Cost" value={`$${(totals.cost_usd ?? 0).toFixed(3)}`} />
        <StatCard label="Intelligent runs" value={intel.total_intelligent ?? 0}
                  hint={`${intel.promote_posted ?? 0} posted · ${intel.karma_farm_completed ?? 0} farmed`} />
      </div>

      <Card title="Tasks per day (stacked)">
        <div style={{ height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={dailyShort} margin={{ top: 8, right: 16, bottom: 0, left: -20 }}>
              <XAxis dataKey="d" stroke={AXIS} fontSize={10} tick={{ fill: AXIS }} tickLine={false} axisLine={{ stroke: GRID }} />
              <YAxis stroke={AXIS} fontSize={10} tick={{ fill: AXIS }} tickLine={false} axisLine={{ stroke: GRID }} allowDecimals={false} />
              <ReTooltip contentStyle={{ background: 'hsl(var(--surface-1))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
              <Bar dataKey="completed" stackId="a" fill="hsl(var(--success))" />
              <Bar dataKey="failed" stackId="a" fill="hsl(var(--danger))" />
              <Bar dataKey="queued" stackId="a" fill="hsl(var(--warning))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Risk score timeline">
          <div style={{ height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={risk} margin={{ top: 8, right: 16, bottom: 0, left: -20 }}>
                <XAxis dataKey="i" stroke={AXIS} fontSize={10} tick={false} axisLine={{ stroke: GRID }} />
                <YAxis domain={[0, 1]} stroke={AXIS} fontSize={10} tick={{ fill: AXIS }} tickLine={false} axisLine={{ stroke: GRID }} />
                <ReTooltip contentStyle={{ background: 'hsl(var(--surface-1))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
                <Line type="monotone" dataKey="risk" stroke="hsl(var(--danger))" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Task kind distribution">
          <div style={{ height: 180 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={kinds} dataKey="count" nameKey="kind" innerRadius={40} outerRadius={70} paddingAngle={2}>
                  {kinds.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <ReTooltip contentStyle={{ background: 'hsl(var(--surface-1))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card title="Intelligent task outcomes">
        <div className="grid gap-3 md:grid-cols-4">
          <MiniStat label="Promote posted" value={intel.promote_posted ?? 0} hue="success" />
          <MiniStat label="Promote skipped" value={intel.promote_skipped ?? 0} hue="warning" />
          <MiniStat label="Promote failed" value={intel.promote_failed ?? 0} hue="danger" />
          <MiniStat label="Karma farmed" value={intel.karma_farm_completed ?? 0} hue="primary" />
        </div>
        {verdicts.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {verdicts.map(v => <span key={v.verdict} className="rounded border border-white/5 bg-[hsl(var(--surface-2))] px-2 py-1 font-mono">{v.verdict} × {v.count}</span>)}
          </div>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
      <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">{title}</div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] p-4">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))]">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular">{value}</div>
      {hint && <div className="text-[11px] text-[hsl(var(--text-3))] mt-0.5">{hint}</div>}
    </div>
  );
}

function MiniStat({ label, value, hue }) {
  const color = hue === 'success' ? 'hsl(var(--success))' : hue === 'warning' ? 'hsl(var(--warning))' : hue === 'danger' ? 'hsl(var(--danger))' : 'hsl(var(--primary))';
  return (
    <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))]">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular" style={{ color }}>{value}</div>
    </div>
  );
}
